import type { FC } from 'react'

import { LoaderCircle, Popup, Table } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LocalDateTime } from '@/components/LocalDateTime'
import { NoResults } from '@/components/NoResults'
import { useRenderLimit } from '@/hooks/use-render-limit'

import queries from './queries'
import salaryDeductionApplicationResources from './resources'
import type { SalaryDeductionPolicyHistoryRow } from './types'

type PolicyHistoryModalProps = {
  costObjectId: number
  onClose: () => void
}

const ACTION_TRANSLATION_IDS: Record<SalaryDeductionPolicyHistoryRow['action'], string> = {
  assigned: 'Assigned',
  removed: 'Removed',
  unlocked: 'Unlocked',
}

export const PolicyHistoryModal: FC<PolicyHistoryModalProps> = ({ costObjectId, onClose }) => {
  const translate = useTranslation()

  const { data, isLoading } = useQuery(
    queries.salaryDeductionPolicyHistory(costObjectId),
    () => salaryDeductionApplicationResources.getPolicyHistory(costObjectId),
    { refetchOnMount: 'always' }
  )
  const { visibleList, ListLoaderComponent } = useRenderLimit(data || [], 20, 20)

  return (
    <Popup variant="wide" ariaLabel={translate('Policy history')} isOpen onClose={onClose}>
      <h2>
        <Translation id="Policy history" />
      </h2>

      {isLoading ? (
        <div className="py-8 text-center">
          <LoaderCircle />
        </div>
      ) : !data || data.length === 0 ? (
        <NoResults />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <th>
                  <Translation id="Policy" />
                </th>
                <th>
                  <Translation id="Action" />
                </th>
                <th>
                  <Translation id="Manager" />
                </th>
                <th>
                  <Translation id="Date" />
                </th>
              </tr>
            </thead>

            <tbody>
              {visibleList.map(row => (
                <tr key={row.id}>
                  <td>{row.policyName}</td>
                  <td>
                    <Translation id={ACTION_TRANSLATION_IDS[row.action]} />
                  </td>
                  <td>{row.managerName || '-'}</td>
                  <td>
                    <LocalDateTime date={row.changedDatetime} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <ListLoaderComponent />
        </>
      )}
    </Popup>
  )
}
